import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import ErrorBoundary from "./components/ErrorBoundary";
import PermissionGate from "./components/PermissionGate";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Items from "./pages/Items";
import ItemDetail from "./pages/ItemDetail";
import Batches from "./pages/Batches";
import Recipes from "./pages/Recipes";
import Production from "./pages/Production";
import ProductionPlanning from "./pages/ProductionPlanning";
import ProductionDemand from "./pages/ProductionDemand";
import AIProductionOptimizer from "./pages/AIProductionOptimizer";
import ProductionBoard from "./pages/ProductionBoard";
import Ausschuss from "./pages/Ausschuss";
import Stock from "./pages/Stock";
import Customers from "./pages/Customers";
import CustomerDetail from "./pages/CustomerDetail";
import B2BPrices from "./pages/B2BPrices";
import Offers from "./pages/Offers";
import Complaints from "./pages/Complaints";
import Suppliers from "./pages/Suppliers";
import Purchase from "./pages/Purchase";
import Deliveries from "./pages/Deliveries";
import Invoices from "./pages/Invoices";
import InvoiceArchive from "./pages/InvoiceArchive";
import Belegscan from "./pages/Belegscan";
import Stornos from "./pages/Stornos";
import Scanner from "./pages/Scanner";
import Users from "./pages/Users";
import Orders from "./pages/Orders";
import Revenue from "./pages/Revenue";
import CalendarPage from "./pages/CalendarPage";
import Monitor from "./pages/Monitor";
import Feedback from "./pages/Feedback";
import Integrations from "./pages/Integrations";
import ReorderSuggestions from "./pages/ReorderSuggestions";
import CustomerStats from "./pages/CustomerStats";
import ShelfView from "./pages/ShelfView";
import Settings from "./pages/Settings";
import LabelDesigner from "./pages/LabelDesigner";
import Pallets from "./pages/Pallets";
import Shipping from "./pages/Shipping";
import ShelvesManage from "./pages/ShelvesManage";
import ShelvesTour from "./pages/ShelvesTour";
import PortalLogin from "./pages/portal/PortalLogin";
import Portal from "./pages/portal/Portal";
import PortalSetPassword from "./pages/portal/PortalSetPassword";
import PortalRegister from "./pages/portal/PortalRegister";

const withLayout = (el, module = null) => (
  <ProtectedRoute>
    <Layout>{module ? <PermissionGate module={module}>{el}</PermissionGate> : el}</Layout>
  </ProtectedRoute>
);

const bare = (el) => <ProtectedRoute>{el}</ProtectedRoute>;

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/feedback/:id" element={<Feedback />} />
            {/* Customer self-service portal — separate auth, no staff layout */}
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal/register" element={<PortalRegister />} />
            <Route path="/portal/set-password" element={<PortalSetPassword />} />
            <Route path="/portal" element={<Portal />} />
            <Route path="/" element={withLayout(<Dashboard />)} />
            <Route path="/bestellungen" element={withLayout(<Orders />, "verkauf")} />
            <Route path="/artikel" element={withLayout(<Items />, "lager")} />
            <Route path="/artikel/:id" element={withLayout(<ItemDetail />, "lager")} />
            <Route path="/chargen" element={withLayout(<Batches />, "lager")} />
            <Route path="/rezepturen" element={withLayout(<Recipes />, "rezepturen")} />
            <Route path="/produktion" element={withLayout(<Production />, "produktion")} />
            <Route path="/produktion/planung" element={withLayout(<ProductionPlanning />, "produktion")} />
            <Route path="/produktion/bedarf" element={withLayout(<ProductionDemand />, "produktion")} />
            <Route path="/produktion/ki" element={withLayout(<AIProductionOptimizer />, "produktion")} />
            <Route path="/produktion/board" element={withLayout(<ProductionBoard />, "produktion")} />
            <Route path="/produktion/monitor" element={withLayout(<Production />, "produktion")} />
            <Route path="/produktion/ausschuss" element={withLayout(<Ausschuss />, "produktion")} />
            <Route path="/kalender" element={withLayout(<CalendarPage />)} />
            <Route path="/lager" element={withLayout(<Stock />, "lager")} />
            <Route path="/lagerbestand" element={<Navigate to="/lager" replace />} />
            <Route path="/lagerbestand/*" element={<Navigate to="/lager" replace />} />
            <Route path="/regale" element={withLayout(<ShelfView />, "lager")} />
            <Route path="/regale/verwalten" element={withLayout(<ShelvesManage />, "lager")} />
            <Route path="/regale/rundgang" element={withLayout(<ShelvesTour />, "lager")} />
            <Route path="/einkauf" element={withLayout(<Purchase />, "einkauf")} />
            <Route path="/bestellvorschlaege" element={withLayout(<ReorderSuggestions />, "einkauf")} />
            <Route path="/kunden" element={withLayout(<Customers />, "crm")} />
            <Route path="/kunden/:id" element={withLayout(<CustomerDetail />, "crm")} />
            <Route path="/reklamationen" element={withLayout(<Complaints />, "crm")} />
            <Route path="/b2b-preise" element={withLayout(<B2BPrices />, "crm")} />
            <Route path="/sonderangebote" element={withLayout(<Offers />, "crm")} />
            <Route path="/lieferanten" element={withLayout(<Suppliers />, "einkauf")} />
            <Route path="/lieferscheine" element={withLayout(<Deliveries />, "verkauf")} />
            <Route path="/versand" element={withLayout(<Shipping />, "versand")} />
            <Route path="/rechnungen" element={withLayout(<Invoices />, "rechnungen")} />
            <Route path="/rechnungsarchiv" element={withLayout(<InvoiceArchive />, "rechnungen")} />
            <Route path="/belegscan" element={withLayout(<Belegscan />, "einkauf")} />
            <Route path="/stornoprotokoll" element={withLayout(<Stornos />, "rechnungen")} />
            <Route path="/paletten" element={withLayout(<Pallets />, "versand")} />
            <Route path="/umsatz" element={withLayout(<Revenue />, "verkauf")} />
            <Route path="/top-kunden" element={withLayout(<CustomerStats />, "verkauf")} />
            <Route path="/scanner" element={withLayout(<Scanner />)} />
            <Route path="/integrationen" element={withLayout(<Integrations />, "einstellungen")} />
            <Route path="/einstellungen" element={withLayout(<Settings />, "einstellungen")} />
            <Route path="/einstellungen/etiketten" element={withLayout(<LabelDesigner />, "labels")} />
            <Route path="/benutzer" element={withLayout(<Users />, "einstellungen")} />
            {/* Full-screen monitor (no sidebar) */}
            <Route path="/monitor" element={bare(<Monitor />)} />
            <Route path="/monitor/:kind" element={bare(<Monitor />)} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
